import { useContent } from 'contexts/Content/Content';
import { getUrl, isStagingHostname } from 'helpers';
import { getCanonicalUrl, getSeoOrigin, toAbsoluteSeoUrl } from 'helpers/seo/seo';
import {
    APP_NAME,
    ORGANIZATION_NAME,
    getLessonFaqStructuredData,
    getLessonKeyFromRoutePath,
    getLessonSeoDescription,
    getLessonSeoTitle,
    isApprovedLessonHeaderKey,
} from 'helpers/seo/toolSeo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

const SOCIAL_IMAGE_PATH = '/assets/landing/illustration.png';

export default function DocHead() {
    const content = useContent();
    const router = useRouter();

    const {
        documentDescription,
        documentTitle,
        favicon,
        siteName,
        socialDescription,
        socialImage,
        socialImageAlt,
        socialTitle,
    } = content.general;

    const routePath = useMemo(() => {
        return (router.asPath || '/').split(/[?#]/)[0] || '/';
    }, [router.asPath]);

    const isAdminRoute = useMemo(() => {
        return routePath.startsWith('/admin');
    }, [routePath]);
    const lessonKey = useMemo(() => {
        return getLessonKeyFromRoutePath(routePath);
    }, [routePath]);

    const isApprovedLessonHeader = useMemo(() => {
        return isApprovedLessonHeaderKey(lessonKey);
    }, [lessonKey]);

    const shouldNoIndexLesson = useMemo(() => {
        return routePath.startsWith('/lessons/') && !isApprovedLessonHeader;
    }, [isApprovedLessonHeader, routePath]);
    const runtimeHostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
    const isStaging = useMemo(() => isStagingHostname(runtimeHostname), [runtimeHostname]);

    const canonicalUrl = useMemo(() => {
        return getCanonicalUrl(routePath, runtimeHostname);
    }, [routePath, runtimeHostname]);

    const rootUrl = useMemo(() => {
        return getCanonicalUrl('/', runtimeHostname);
    }, [runtimeHostname]);

    const faviconUrl = useMemo(() => {
        return toAbsoluteSeoUrl(getUrl(favicon), runtimeHostname);
    }, [favicon, runtimeHostname]);

    const pageTitle = useMemo(() => {
        if (isAdminRoute) {
            return `${APP_NAME} Admin | ${ORGANIZATION_NAME}`;
        }

        return getLessonSeoTitle(routePath, content.lessons, documentTitle);
    }, [content.lessons, documentTitle, isAdminRoute, routePath]);

    const shareTitle = useMemo(() => {
        if (pageTitle !== documentTitle || isAdminRoute) {
            return pageTitle;
        }

        return socialTitle || documentTitle;
    }, [documentTitle, isAdminRoute, pageTitle, socialTitle]);
    const pageDescription = useMemo(() => {
        return getLessonSeoDescription(routePath, content.lessons, documentDescription);
    }, [content.lessons, documentDescription, routePath]);

    const shareDescription = useMemo(() => {
        if (isApprovedLessonHeader) {
            return pageDescription;
        }

        return socialDescription || pageDescription;
    }, [isApprovedLessonHeader, pageDescription, socialDescription]);

    const socialImageAltText = useMemo(() => {
        return socialImageAlt || `${APP_NAME} preview illustration from ${siteName || ORGANIZATION_NAME}`;
    }, [siteName, socialImageAlt]);

    const socialImageUrl = useMemo(() => {
        return toAbsoluteSeoUrl(getUrl(socialImage || SOCIAL_IMAGE_PATH), runtimeHostname);
    }, [runtimeHostname, socialImage]);

    const faqStructuredData = useMemo(() => {
        return getLessonFaqStructuredData(routePath, content.lessons, runtimeHostname);
    }, [content.lessons, routePath, runtimeHostname]);

    const structuredData = useMemo(() => {
        const siteOrigin = getSeoOrigin(runtimeHostname);
        const websiteId = `${rootUrl}#website`;
        const organizationId = `${siteOrigin}#organization`;
        const applicationId = `${rootUrl}#webapplication`;
        const featureList = [
            content.landing.point1?.label,
            content.landing.point2?.label,
            content.landing.point3?.label,
            content.landing.point4?.label,
        ].filter(Boolean);

        return {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@id': organizationId,
                    '@type': 'Organization',
                    name: ORGANIZATION_NAME,
                    logo: faviconUrl,
                    url: siteOrigin,
                },
                {
                    '@id': websiteId,
                    '@type': 'WebSite',
                    name: APP_NAME,
                    description: documentDescription,
                    publisher: {
                        '@id': organizationId,
                    },
                    url: rootUrl,
                },
                {
                    '@id': applicationId,
                    '@type': 'WebApplication',
                    applicationCategory: 'EducationalApplication',
                    description: documentDescription,
                    featureList,
                    image: socialImageUrl,
                    name: APP_NAME,
                    operatingSystem: 'Web',
                    publisher: {
                        '@id': organizationId,
                    },
                    url: rootUrl,
                },
                {
                    '@id': `${canonicalUrl}#webpage`,
                    '@type': 'WebPage',
                    about: {
                        '@id': applicationId,
                    },
                    description: shareDescription,
                    image: socialImageUrl,
                    isPartOf: {
                        '@id': websiteId,
                    },
                    name: pageTitle,
                    url: canonicalUrl,
                },
                ...(faqStructuredData ? [faqStructuredData] : []),
            ],
        };
    }, [
        canonicalUrl,
        content.landing.point1,
        content.landing.point2,
        content.landing.point3,
        content.landing.point4,
        documentDescription,
        faqStructuredData,
        faviconUrl,
        pageTitle,
        rootUrl,
        runtimeHostname,
        shareDescription,
        socialImageUrl,
    ]);

    if (isAdminRoute) {
        return (
            <Head>
                <title>{pageTitle}</title>
                <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
                <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
                <link rel="shortcut icon" href={faviconUrl} />
            </Head>
        );
    }

    if (isStaging) {
        return (
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="robots" content="noindex, nofollow" />
                <meta name="googlebot" content="noindex, nofollow" />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="shortcut icon" href={faviconUrl} />
            </Head>
        );
    }

    if (shouldNoIndexLesson) {
        return (
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={documentDescription} />
                <meta name="robots" content="noindex, follow" />
                <meta name="googlebot" content="noindex, follow" />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="shortcut icon" href={faviconUrl} />
            </Head>
        );
    }

    return (
        <Head>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            <link rel="canonical" href={canonicalUrl} />
            <meta property="og:title" content={shareTitle} />
            <meta property="og:description" content={shareDescription} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content={siteName || ORGANIZATION_NAME} />
            <meta property="og:image" content={socialImageUrl} />
            <meta property="og:image:alt" content={socialImageAltText} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={shareTitle} />
            <meta name="twitter:description" content={shareDescription} />
            <meta name="twitter:image" content={socialImageUrl} />
            <meta name="twitter:image:alt" content={socialImageAltText} />
            <link rel="shortcut icon" href={faviconUrl} />
            <script
                type="application/ld+json"
                // JSON-LD must be emitted as a string for search engine parsers.
                dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
            />
        </Head>
    );
}
